import React from 'react';
import { Template } from './Template';
import { AddressBlock, InputText } from '@/components/form';
import {
	FormItem,
	FormInputEmails,
	FormInputPhone,
	FormInputPicture,
	ModalDelete,
} from '@/components/dashboard/settings';
import { UserI } from '@/interfaces';
import Image from 'next/image';
import Script from 'next/script';
import { Icon, Typography } from '@/components/common';
import { InfoCircle } from '@/public/icons/tsx';
import Icons from '@/const/icons';
import { Alert } from 'components/common/alerts/alerts';
import { usePersonalInformation } from '@/hooks/usePersonalInformation';

interface PersonalDetailsProps {
	id: string;
	userInformation?: UserI;
	refetch: () => void;
}

export const PersonalDetails: React.FC<PersonalDetailsProps> = ({
	id,
	userInformation,
	refetch,
}) => {
	// Step 1 (verified email + first/last name) must be met before the rest
	// of the form becomes required.
	const step1Complete =
		!!userInformation?.is_verified &&
		!!userInformation?.firstname &&
		!!userInformation?.lastname;

	const {
		rules,
		handleSave,
		setInitialData,
		isVerified,
		setAddSecondEmail,
		removedPrevFile,
		previousFile,
		setRemovedPrevFile,
		addSecondEmail,
		initialAdditionalEmail,
		initialPhone,
		setIsOpenModalDelete,
		isOpenModalDelete,
		form,
		setSelectedGoogleLocation,
		clearSelectedGoogleLocation,
	} = usePersonalInformation({
		id,
		userInformation,
		refetch,
		requireAddress: step1Complete,
	});

	const {
		register,
		setValue,
		watch,
		formState: { errors },
		handleSubmit,
	} = form;

	return (
		<Template
			title="Your Personal Information"
			formId="your-personal-information-form"
			subtitleCustom={
				// CLAIM-3486 — first/last name are no longer required, so this
				// message now covers email verification only (the profile
				// completion framing was removed).
				!isVerified ? (
					<div className="flex items-center gap-x-1 mt-1">
						<Icon
							src={Icons.danger}
							width={16}
							height={16}
							className="shrink-0"
						/>
						<Typography type="regular-14" className="text-main-red">
							Verify your email to move forward with your case.
						</Typography>
					</div>
				) : undefined
			}
			onClickSave={handleSubmit(handleSave)}
			onClickCancel={() => {
				if (!userInformation?.additional_email) {
					setAddSecondEmail(false);
				} else {
					setAddSecondEmail(true);
				}
				clearSelectedGoogleLocation();
				setInitialData();
			}}
		>
			{userInformation?.role === 'client' && (
				<Alert
					type="warning"
					text="Important Legal Notice"
					className="my-5 px-4"
					classNameText="!text-black !font-semibold"
					description="The information provided in this section is used for official legal claims and documentation. You must ensure that all details entered are strictly accurate and truthful. Providing false, misleading, or fraudulent information may result in the immediate dismissal of your cases."
				/>
			)}
			<FormItem label="Profile picture">
				<FormInputPicture
					register={register}
					setValue={setValue}
					file={watch('file')}
					prevFile={!removedPrevFile ? previousFile : ''}
					removePrevFile={() => setRemovedPrevFile(true)}
					error={errors.file}
					firstname={userInformation?.firstname || ''}
					lastname={userInformation?.lastname || ''}
				/>
			</FormItem>
			<FormItem label="Name">
				<div className="flex flex-col md:flex-row items-baseline gap-4 w-full">
					<InputText
						name="firstName"
						register={register}
						placeholder="Ex: Sarah"
						className="w-full md:w-[240px]"
						classNameInput="!mt-0"
						rules={rules.firstName}
						error={errors.firstName}
					/>
					<InputText
						name="lastName"
						register={register}
						placeholder="Ex: Willson"
						className="w-full md:w-[240px]"
						classNameInput="!mt-0"
						rules={rules.lastName}
						error={errors.lastName}
					/>
				</div>
			</FormItem>
			<FormItem label="Email address" className="!items-baseline">
				{userInformation && (
					<FormInputEmails
						role={userInformation?.role}
						refetch={refetch}
						register={register}
						setValue={setValue}
						additionalEmail={watch('additionalEmail')}
						setAddSecondEmail={setAddSecondEmail}
						addSecondEmail={addSecondEmail}
						initialAdditionalEmail={initialAdditionalEmail}
						isSecondEmailVerified={
							!!userInformation?.additional_email &&
							userInformation?.is_additional_email_verified
						}
						error={errors.additionalEmail}
					/>
				)}
			</FormItem>
			<FormItem label="Phone">
				<FormInputPhone
					refetch={refetch}
					register={register}
					setValue={setValue}
					phoneNumber={watch('phone')}
					initialPhoneValue={initialPhone}
					error={errors.phone}
					isPhoneVerified={userInformation?.is_phone_number_verified}
					rules={step1Complete ? rules.phone : undefined}
				/>
				{!userInformation?.phonenumber && (
					<div className="flex items-center gap-x-1.5 mt-1">
						<InfoCircle
							fill="#2D41F5"
							width="16"
							height="16"
							className="flex-shrink-0"
						/>
						<span className="flex items-center mt-1 gap-1">
							<Typography type="regular-12" className="text-main-blue-dark">
								Provide your phone number to help us offer faster assistance.
							</Typography>
						</span>
					</div>
				)}
			</FormItem>
			{userInformation && (
				<AddressBlock
					register={register}
					setValue={setValue}
					watch={watch}
					errors={errors}
					rules={rules}
					defaultAddress1={userInformation.address1 || ''}
					initialCityName={userInformation.city?.name || ''}
					initialStateName={userInformation.state?.name || ''}
					initialZip={userInformation.zip_code || ''}
					onGoogleSelect={(address) => {
						setSelectedGoogleLocation({
							state: address.state,
							city: address.city,
							zip: address.zip,
						});
					}}
				/>
			)}
			{userInformation?.role === 'client' && (
				<FormItem label="Account" className="!border-none !pb-0 !pt-5">
					<button
						type="button"
						className="flex items-center gap-2 xl:ml-6"
						onClick={() => setIsOpenModalDelete(true)}
					>
						<Image src="/img/common/trash.png" alt="" width={16} height={16} />
						<Typography type="semibold-16" className="text-main-red">
							Delete account
						</Typography>
					</button>

					<ModalDelete
						isOpen={isOpenModalDelete}
						setModalOpen={setIsOpenModalDelete}
					/>
				</FormItem>
			)}

			{process.env.NEXT_PUBLIC_SERVER === 'production' && (
				<Script
					id="PD_Script_Configs"
					src="https://forms.adq.us.cdn-world.net/CLA/configs-form.js"
					strategy="afterInteractive"
				/>
			)}
		</Template>
	);
};
